import type { CreateOrgUserRequest } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { type LegacyCharity, RequestError, api } from '../../lib/api'
import { ROLE_FA, STATUS_FA, faDate, faNum } from './labels'

type Target = 'existing_org' | 'legacy_charity' | 'new_org'

/**
 * Charity accounts: who exists, and how a new one is made.
 *
 * This is the only way an account comes to exist: nobody registers themselves.
 * The admin makes it here and hands over the email and password. The three ways
 * in are the three facts about a charity: it is already here, it is in the portal
 * extract, or it is new to both.
 */
export function AccountsTab({ canManage }: { canManage: boolean }) {
  const [search, setSearch] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const users = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => api.adminUsers({ search: search.trim() || undefined }),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof RequestError ? err.messageFa : fallback)

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'disabled' }) =>
      api.adminSetUserStatus(id, status),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: (err) => fail(err, 'تغییر وضعیت انجام نشد.'),
  })

  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      api.adminResetPassword(id, password),
    onSuccess: () => {
      setError(null)
      setNotice('گذرواژه عوض شد. همهٔ نشست‌های باز این حساب بسته شد.')
      refresh()
    },
    onError: (err) => fail(err, 'گذرواژه عوض نشد.'),
  })

  return (
    <div className="stack">
      {error && <p className="notice notice--error">{error}</p>}
      {notice && <p className="notice notice--teach">{notice}</p>}

      {canManage && (
        <CreateAccountCard
          onCreated={(message) => {
            setNotice(message)
            setError(null)
            refresh()
          }}
          onError={(message) => setError(message)}
        />
      )}

      <div className="card">
        <h2>حساب‌ها</h2>
        <div className="field">
          <label htmlFor="user-search">جست‌وجو</label>
          <input
            id="user-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ایمیل یا نام"
          />
        </div>

        {users.isLoading && <p className="muted">در حال بارگذاری…</p>}
        {users.data && users.data.users.length === 0 && <p className="muted">حسابی پیدا نشد.</p>}

        {users.data && users.data.users.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>حساب</th>
                  <th>مؤسسه</th>
                  <th>نقش</th>
                  <th>آخرین ورود</th>
                  <th>وضعیت</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="ltr">{user.email}</div>
                      <div className="meta">
                        {user.displayName ?? 'بدون نام'}
                        {user.mustChangePassword && ' · باید گذرواژه‌اش را عوض کند'}
                      </div>
                    </td>
                    <td>{user.orgName}</td>
                    <td>{ROLE_FA[user.role]}</td>
                    <td>{faDate(user.lastLoginOn)}</td>
                    <td>
                      <span
                        className={user.status === 'active' ? 'badge badge--good' : 'badge badge--bad'}
                      >
                        {STATUS_FA[user.status] ?? user.status}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="table__actions">
                          <button
                            type="button"
                            className="btn btn--quiet btn--sm"
                            onClick={() =>
                              setStatus.mutate({
                                id: user.id,
                                status: user.status === 'active' ? 'disabled' : 'active',
                              })
                            }
                          >
                            {user.status === 'active' ? 'غیرفعال کردن' : 'فعال کردن'}
                          </button>
                          <PasswordButton
                            pending={resetPassword.isPending}
                            onSubmit={(password) => resetPassword.mutate({ id: user.id, password })}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function PasswordButton({
  pending,
  onSubmit,
}: {
  pending: boolean
  onSubmit: (password: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')

  if (!open) {
    return (
      <button type="button" className="btn btn--quiet btn--sm" onClick={() => setOpen(true)}>
        گذرواژهٔ تازه
      </button>
    )
  }

  return (
    <div className="row">
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="دست‌کم ۱۲ نویسه"
        style={{ width: '14rem' }}
      />
      <button
        type="button"
        className="btn btn--sm"
        disabled={pending || password.length < 12}
        onClick={() => {
          onSubmit(password)
          setPassword('')
          setOpen(false)
        }}
      >
        ثبت
      </button>
      <button type="button" className="btn btn--quiet btn--sm" onClick={() => setOpen(false)}>
        بی‌خیال
      </button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function CreateAccountCard({
  onCreated,
  onError,
}: {
  onCreated: (message: string) => void
  onError: (message: string) => void
}) {
  const [target, setTarget] = useState<Target>('existing_org')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<'member' | 'org_admin'>('org_admin')
  const [orgId, setOrgId] = useState<number | null>(null)
  const [orgName, setOrgName] = useState('')
  const [charity, setCharity] = useState<LegacyCharity | null>(null)
  /* The admin's call, shown rather than assumed: an old questionnaire can be
     years out of date or filled in by somebody who has left. Both start ticked
     when there is something to bring, because the usual answer is yes. */
  const [importAssessment, setImportAssessment] = useState(false)
  const [importProfile, setImportProfile] = useState(false)

  const orgs = useQuery({ queryKey: ['admin-orgs', ''], queryFn: () => api.adminOrgs() })

  const create = useMutation({
    mutationFn: (input: CreateOrgUserRequest) => api.adminCreateUser(input),
    onSuccess: (result) => {
      const where = result.createdOrg ? 'مؤسسه‌اش هم ساخته شد' : 'به مؤسسهٔ موجود اضافه شد'
      const seeded =
        result.backfilled > 0
          ? ` و ${faNum(result.backfilled)} مورد از خودارزیابی قبلی‌اش آورده شد`
          : ''
      const profiled =
        result.factsWritten > 0 ? ` و ${faNum(result.factsWritten)} مورد به شناسنامه‌اش اضافه شد` : ''
      onCreated(`حساب ${result.user.email} ساخته شد؛ ${where}${seeded}${profiled}.`)
      setEmail('')
      setPassword('')
      setDisplayName('')
      setOrgName('')
      setCharity(null)
      setImportAssessment(false)
      setImportProfile(false)
    },
    onError: (err) => onError(err instanceof RequestError ? err.messageFa : 'حساب ساخته نشد.'),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate({
      email,
      password,
      displayName: displayName.trim() || undefined,
      role,
      mustChangePassword: true,
      orgId: target === 'existing_org' ? orgId : null,
      legacyCharityId: target === 'legacy_charity' ? (charity?.id ?? null) : null,
      orgName: target === 'new_org' ? orgName.trim() : null,
      importAssessment: target === 'legacy_charity' && importAssessment,
      importProfile: target === 'legacy_charity' && importProfile,
    })
  }

  const ready =
    email.includes('@') &&
    password.length >= 12 &&
    ((target === 'existing_org' && orgId !== null) ||
      (target === 'legacy_charity' && charity !== null) ||
      (target === 'new_org' && orgName.trim().length > 1))

  return (
    <div className="card">
      <h2>ساختن حساب تازه</h2>
      <p className="meta">
        گذرواژه را خودتان می‌گذارید و یک بار به صاحب حساب می‌دهید؛ سامانه از او می‌خواهد در اولین
        ورود عوضش کند.
      </p>

      <form onSubmit={submit}>
        <div className="field">
          <span className="label">این حساب به کجا دسترسی دارد؟</span>
          <div className="options">
            <TargetOption
              value="existing_org"
              current={target}
              onPick={setTarget}
              title="مؤسسه‌ای که در سامانه هست"
              note="به همان مؤسسه و همان سابقه اضافه می‌شود."
            />
            <TargetOption
              value="legacy_charity"
              current={target}
              onPick={setTarget}
              title="مؤسسه‌ای از فهرست افرا"
              note="اگر هنوز اینجا نباشد ساخته می‌شود. خودتان انتخاب می‌کنید داده‌های قبلی‌اش بیاید یا نه."
            />
            <TargetOption
              value="new_org"
              current={target}
              onPick={setTarget}
              title="مؤسسهٔ تازه، بدون سابقه"
              note="یک مؤسسهٔ خالی با نامی که می‌نویسید."
            />
          </div>
        </div>

        {target === 'existing_org' && (
          <div className="field">
            <label htmlFor="org-pick">مؤسسه</label>
            <select
              id="org-pick"
              value={orgId ?? ''}
              onChange={(e) => setOrgId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">انتخاب کنید…</option>
              {orgs.data?.orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {target === 'legacy_charity' && (
          <CharityPicker
            selected={charity}
            onSelect={(c) => {
              setCharity(c)
              setImportAssessment(c !== null && c.evaluations > 0)
              setImportProfile(c !== null)
            }}
          />
        )}

        {/* Only a new organization takes a choice here. One that already exists
            keeps what it has; refreshing it is done from the organization list. */}
        {target === 'legacy_charity' && charity !== null && !charity.claimed && (
          <div className="field">
            <span className="label">از داده‌های قبلی این مؤسسه در پرتال چه چیزی بیاید؟</span>
            <label className="check check--plain">
              <input
                type="checkbox"
                checked={importAssessment}
                onChange={(e) => setImportAssessment(e.target.checked)}
              />
              <span>جواب‌های خودارزیابی قبلی</span>
              <span className="check__hint">
                {charity.evaluations > 0
                  ? `از ${faNum(charity.evaluations)} ارزیابی ثبت‌شده. به‌عنوان «تأییدنشده» می‌آیند و افرا در گفت‌وگو از همان‌جا شروع می‌کند، نه از صفر.`
                  : 'این مؤسسه در پرتال ارزیابی ثبت‌شده‌ای ندارد.'}
              </span>
            </label>
            <label className="check check--plain">
              <input
                type="checkbox"
                checked={importProfile}
                onChange={(e) => setImportProfile(e.target.checked)}
              />
              <span>اطلاعات شناسنامه</span>
              <span className="check__hint">
                شمار کارکنان و داوطلبان، درآمد سالانه، شهر، مأموریت و فعالیت‌ها، هر کدام که در پرتال ثبت شده باشد.
              </span>
            </label>
            <span className="meta">هر دو را بعداً هم می‌شود از فهرست مؤسسه‌ها آورد یا به‌روز کرد.</span>
          </div>
        )}

        {target === 'new_org' && (
          <div className="field">
            <label htmlFor="new-org-name">نام مؤسسه</label>
            <input id="new-org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          </div>
        )}

        <div className="grid-2">
          <div className="field">
            <label htmlFor="new-email">ایمیل</label>
            <input
              id="new-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-password">گذرواژه</label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="دست‌کم ۱۲ نویسه"
            />
          </div>
          <div className="field">
            <label htmlFor="new-name">نام صاحب حساب</label>
            <input
              id="new-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-role">نقش در مؤسسه</label>
            <select
              id="new-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'member' | 'org_admin')}
            >
              <option value="org_admin">{ROLE_FA.org_admin}</option>
              <option value="member">{ROLE_FA.member}</option>
            </select>
          </div>
        </div>

        <button type="submit" className="btn" disabled={!ready || create.isPending}>
          {create.isPending ? 'در حال ساختن…' : 'حساب را بساز'}
        </button>
      </form>
    </div>
  )
}

function TargetOption({
  value,
  current,
  onPick,
  title,
  note,
}: {
  value: Target
  current: Target
  onPick: (t: Target) => void
  title: string
  note: string
}) {
  return (
    <button
      type="button"
      className={`option ${current === value ? 'is-selected' : ''}`}
      onClick={() => onPick(value)}
    >
      <span>
        <span style={{ fontWeight: 700 }}>{title}</span>
        <span className="meta" style={{ display: 'block' }}>
          {note}
        </span>
      </span>
    </button>
  )
}

/**
 * Searching the portal extract. The list is 1,937 charities long, so it is a
 * search and not a dropdown, and a charity that is already here says so —
 * picking it adds an account to the existing tenant rather than a second one.
 */
function CharityPicker({
  selected,
  onSelect,
}: {
  selected: LegacyCharity | null
  onSelect: (c: LegacyCharity | null) => void
}) {
  const [term, setTerm] = useState('')
  const results = useQuery({
    queryKey: ['admin-legacy', term],
    queryFn: () => api.adminLegacyCharities(term.trim()),
    enabled: term.trim().length >= 2,
  })

  if (selected) {
    return (
      <div className="field">
        <span className="label">مؤسسه</span>
        <div className="row">
          <span>{selected.title}</span>
          <span className="meta">
            <span className="num">{faNum(selected.evaluations)}</span> ارزیابی در پرتال
          </span>
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => onSelect(null)}>
            تغییر
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="field">
      <label htmlFor="charity-search">مؤسسه را از فهرست افرا پیدا کنید</label>
      <input
        id="charity-search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="بخشی از نام"
      />
      {results.data && results.data.charities.length > 0 && (
        <div className="options" style={{ marginTop: 'var(--space-3)' }}>
          {results.data.charities.slice(0, 8).map((c) => (
            <button
              key={c.id}
              type="button"
              className="option"
              onClick={() => onSelect(c)}
            >
              <span>
                <span>{c.title}</span>
                <span className="meta" style={{ display: 'block' }}>
                  <span className="num">{faNum(c.evaluations)}</span> ارزیابی
                  {c.claimed && ' · همین حالا در سامانه هست'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
